import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { fadeInUp, containerVariants } from '../constants/animations';
import { buildApiUrl } from '../utils/api';
import FooterSection from '../features/home/components/FooterSection';
import './Blog.css';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(buildApiUrl('/api/blog'))
      .then((r) => r.json())
      .then((data) => {
        setPosts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load posts.');
        setLoading(false);
      });
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <main className="blog-page">
      <Helmet>
        <title>Blog | Versa Launch</title>
        <meta
          name="description"
          content="Growth tips, digital marketing insights, and industry deep-dives from the Versa Launch team."
        />
      </Helmet>

      <section className="blog-hero">
        <div className="container">
          <motion.span
            className="blog-kicker"
            variants={fadeInUp}
            custom={0}
            initial="hidden"
            animate="visible"
          >
            Insights &amp; Strategy
          </motion.span>
          <motion.h1 variants={fadeInUp} custom={1} initial="hidden" animate="visible">
            The Versa Launch <span className="blog-gold">Blog</span>
          </motion.h1>
          <motion.p variants={fadeInUp} custom={2} initial="hidden" animate="visible">
            Growth tips, digital marketing insights, and industry deep-dives to help your business dominate.
          </motion.p>
        </div>
      </section>

      <section className="blog-posts-section">
        <div className="container">
          {loading && (
            <div className="blog-loading">
              <div className="blog-spinner" />
            </div>
          )}

          {!loading && error && <p className="blog-error">{error}</p>}

          {!loading && !error && posts.length === 0 && (
            <div className="blog-empty">
              <p>No posts yet - check back soon.</p>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <motion.div
              className="blog-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {posts.map((post, i) => (
                <motion.article key={post.slug} className="blog-card" variants={fadeInUp} custom={i}>
                  {post.hero_image_url && (
                    <div className="blog-card-image">
                      <img src={post.hero_image_url} alt={post.title} loading="lazy" />
                    </div>
                  )}
                  <div className="blog-card-body">
                    <div className="blog-card-date">
                      <Calendar size={14} />
                      <span>{formatDate(post.published_at)}</span>
                    </div>
                    <h2>{post.title}</h2>
                    {post.meta_description && <p>{post.meta_description}</p>}
                    <Link to={`/blog/${post.slug}`} className="blog-card-link">
                      Read More <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      <FooterSection />
    </main>
  );
}

export default Blog;
